import React from "react";
import { Alert, Box } from "@mui/material";
import { VideoPlayback } from "../../utils/videoPlayback";

interface VideoPlayerProps {
  /** Resolved by `resolveVideoPlayback()` — never build URLs at the call site. */
  playback: VideoPlayback;
  title?: string;
  /** Poster frame for the file player; ignored by the Stream embed. */
  poster?: string;
  /** Shown when there is nothing to play. */
  unavailableMessage?: string;
  autoPlay?: boolean;
}

/**
 * Plays a video with whichever player its source needs — the Cloudflare Stream
 * embed for Stream assets, a native `<video>` element for plain media files.
 *
 * Fills its container, so give the parent the aspect ratio you want.
 */
export const VideoPlayer: React.FC<VideoPlayerProps> = ({
  playback,
  title = "Video",
  poster,
  unavailableMessage,
  autoPlay = false,
}) => {
  if (playback.kind === "stream") {
    return (
      <iframe
        src={playback.iframeUrl}
        title={title}
        allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
        allowFullScreen
        style={{ width: "100%", height: "100%", border: "none", display: "block" }}
      />
    );
  }

  if (playback.kind === "file") {
    return (
      <Box
        component="video"
        src={playback.fileUrl}
        poster={poster || undefined}
        controls
        playsInline
        autoPlay={autoPlay}
        controlsList="nodownload"
        sx={{ width: "100%", height: "100%", display: "block", bgcolor: "#000" }}
      />
    );
  }

  if (!unavailableMessage) return null;

  return (
    <Alert severity="warning" sx={{ m: 2 }}>
      {unavailableMessage}
    </Alert>
  );
};

export default VideoPlayer;
